import i18n from '@/plugins/i18n';
import { licenseStates, licenseFailureDetails, licenseErrorCode, LICENSE_TYPES } from '@/const/licensePage';
import router from '@/router';
import apolloClient from '@/plugins/GraphQL/Apollo/client';
import { CommonDataDocument } from '@/graphql/codegen/generated/graphql';
import { AgentCsvReport } from '@/services/csv';
import { app } from '@/bootloader';
import en from '@/locales/en';

export const dialogPresets = {
  computed: {
    dialogPresets() {
      return {
        agentsBulkActionPartialSuccessOrFail: (results: AgentCsvReport[], updatedAgentsCount: any) => {
          if (updatedAgentsCount > 0 && updatedAgentsCount < results.length) {
            this.openDialog('NewWarningDialog', {
              width: 339,
              height: 400,
              template: {
                dialogTitle: i18n.global.t('agentsTable.postActionDialogText.dialog.warning.title'),
                body: i18n.global.t('agentsTable.postActionDialogText.dialog.warning.body', [results.length - updatedAgentsCount, results.length]),
                okLabel: i18n.global.t('agentsTable.postActionDialogText.dialog.warning.btn'),
              },
              iconClose: true,
              exportToCsv: () => this.agentsRequestsReport(results),
              okCallback: () => null,
              cancelCallback: () => null,
            });
          } else {
            this.openDialog('NewErrorDialog', {
              width: 339,
              height: 400,
              template: {
                dialogTitle: i18n.global.t('agentsTable.postActionDialogText.dialog.error.title'),
                body: i18n.global.t('agentsTable.postActionDialogText.dialog.error.body', [results.length]),
                okLabel: i18n.global.t('agentsTable.postActionDialogText.dialog.error.btn'),
              },
              iconClose: true,
              exportToCsv: () => this.agentsRequestsReport(results),
              okCallback: () => null,
              cancelCallback: () => null,
            });
          }
        },
        resolveAgentsDialog: (okCallback = () => {}, cancelCallback = () => {}) => {
          this.openDialog('AgentActionNotAllowedDialog', {
            dialogTitle: {
              content: i18n.global.t('agentsTable.duplicateLicense.dialog.title'),
              class: 'ibf-h2',
              style: {
                margin: 'auto',
              },
            },
            body: {
              content: i18n.global.t('agentsTable.duplicateLicense.dialog.body'),
              class: 'ibf-h6 app_text_dark',
              style: {
                textAlign: 'center',
              },
            },
            template: {
              cancelLabel: 'Cancel',
              okLabel: 'Approve',
              okType: 'information',
              cancelType: 'regular',
            },
            iconClose: false,
            attrs: {},
            width: 432,
            height: 269,
            okCallback,
            cancelCallback,
          });
        },
        releaseAgentPopupDialog: (okCallback = () => {}, cancelCallback = () => {}) => {
          this.openDialog('SuccessDialogV2', {
            template: {
              okLabel: 'Yes',
              cancelLabel: 'No',
              bodyIcon: false, // Optional defualt is true
            },
            attrs: {},
            body: {
              content: 'Are you sure you want to release your cloud agents?',
              class: 'ibf-h6 app_text_dark',
              style: {
                margin: 'auto',
              },
            },
            dialogTitle: {
              content: 'Cloud Agents Release',
              class: 'ibf-h2',
              style: {
                margin: 'auto',
              },
            },
            width: 432,
            height: 197,
            okCallback,
            cancelCallback,
          });
        },
        successSaveSettingsPage: (resultField: keyof typeof en.settings.dialogs) => {
          this.openDialog('SuccessDialogV2', {
            template: {
              bodyIcon: false, // Optional defualt is true
            },
            attrs: {},
            body: {
              content: i18n.global.t(`settings.dialogs.${resultField}.body`),
              class: 'ibf-h6 app_text_dark',
              style: {
                textAlign: 'center',
                marginTop: '4px',
              },
            },
            dialogTitle: {
              content: i18n.global.t(`settings.dialogs.${resultField}.title`),
              class: 'ibf-h2',
              style: {
                margin: 'auto',
              },
            },
            width: 432,
            height: 197,
            okCallback: () => null,
            cancelCallback: () => null,
            locked: true,
          });
        },
        forgotPassword: (link: string) => {
          this.openDialog('SuccessDialogV2', {
            template: {
              okLabel: 'Got it',
              bodyIcon: false, // Optional defualt is true
            },
            attrs: {},
            dialogTitle: {
              content: i18n.global.t('login.loginForm.dialog.forgotPassword.title'),
              class: 'ibf-h2',
              style: {
                paddingTop: '13px',
                margin: 'auto',
              },
            },
            body: {
              content: i18n.global.t('login.loginForm.dialog.forgotPassword.body').replace(`$LINK`, link),
              class: 'ibf-h5 app_text_dark',
              style: {
                margin: '4px 16px 0px',
                textAlign: 'center',
              },
            },

            width: 432,
            height: 244,
            okCallback: () => null,
            cancelCallback: () => null,
          });
        },
        pleaseHold: () =>
          this.openDialog('SuccessDialogV2', {
            template: {
              spinnerDots: true,
              bodyIcon: false, // Optional defualt is true
            },
            attrs: {},
            body: {
              content: i18n.global.t('agents.dialogs.pleaseHold.body'),
              class: 'ibf-h3 app_text_dark',
              style: {
                margin: 'auto',
              },
            },
            dialogTitle: {
              content: i18n.global.t('agents.dialogs.pleaseHold.title'),
              class: 'ibf-h1',
              style: {
                margin: 'auto',
              },
            },
            width: 339,
            height: 254,
            okCallback: () => null,
            cancelCallback: () => null,
          }),
        setRegistryKey: (agentsArray: any) => {
          this.openDialog('SetRegistryConfigurationDialog', {
            width: 400,
            height: 420,
            iconClose: true,
            agents: agentsArray,
          });
        },
        handleLicenseActivationErrorDialogs(openDialogFunction: any, stateInfo: any) {
          const openDialogFunctionHandler = typeof openDialogFunction === 'function' ? openDialogFunction : (this as any).openDialog;
          let title = null;
          let body = null;
          switch (stateInfo.errorCode) {
            case 0:
              break;
            case 1: {
              title = i18n.global.t('generic.linkDown.networkFailure.title');
              body = i18n.global.t('generic.linkDown.networkFailure.body');
              break;
            }
            case 2: {
              title = i18n.global.t('generic.linkDown.unexpectedError.title');
              body = '';
              break;
            }
            case 3: {
              title = i18n.global.t('generic.linkDown.serviceDown.title');
              body = i18n.global.t('generic.linkDown.serviceDown.body');
              break;
            }
            case 4: {
              title = i18n.global.t('generic.linkDown.trialExpired.title');
              body = i18n.global.t('generic.linkDown.trialExpired.body');
              break;
            }
            default:
              break;
          }
          if (body !== null && title !== null) openDialogFunctionHandler('LinkDownMessage', { grayBackground: true, width: 0, height: 0, title, body });
        },

        handleLicenseStateInfo(openDialogFunction: any, stateInfo: any, licenseKey = undefined) {
          const openDialogFunctionHandler = typeof openDialogFunction === 'function' ? openDialogFunction : (this as any).openDialog;

          const isLicenseKeyActivated = !!licenseKey;
          if (stateInfo) {
            if (stateInfo?.errorCode && stateInfo?.errorCode !== licenseErrorCode.No_Error) {
              // 0-No Error  1-Network Error  2-Unexpected Error 3-ServiceDown 4-trialExpired
              if (stateInfo?.errorCode === licenseErrorCode.Trial_Expired) {
                // Trial Failure
                openDialogFunctionHandler('LicenseTrialFailureDialog', {
                  grayBackground: true,
                  width: 656,
                  height: 720,
                });
              } else
                openDialogFunctionHandler('LicenseFailureDialog', {
                  grayBackground: true,
                  width: 656,
                  height: 720,
                  errorDetails: (licenseFailureDetails as any)[stateInfo?.errorCode],
                  isRefreshLicense: isLicenseKeyActivated,
                  iconCloseBig: isLicenseKeyActivated,
                });
            } else this.handleLicenseStatesDialog(stateInfo?.state, openDialogFunctionHandler, isLicenseKeyActivated as any);
          }
        },
        async handleLicenseStatesDialog(licenseStateCode: any, openDialogFunction = undefined, licenseKeyActivated = undefined) {
          const openDialogFunctionHandler = typeof openDialogFunction === 'function' ? openDialogFunction : (this as any).openDialog;
          const GQL_useLicenseInfoQuery = await apolloClient.query({
            query: CommonDataDocument,
            variables: {
              coordinatorId: app.config.globalProperties.coordinatorService.selectedCoordinator.value,
            },
          });
          const { license } = GQL_useLicenseInfoQuery?.data?.ibManagerEntities || '';
          switch (licenseStateCode) {
            case 0: // None
              await router.push({ name: 'enterLicense' });
              break;
            case 1: // Active
              break;
            case 2: // Disabled
              if (licenseKeyActivated && license?.type === LICENSE_TYPES.Marketplace)
                // MarketPlace dialog in case of validation
                openDialogFunctionHandler('LicenseMarketplaceDialog', {
                  grayBackground: true,
                  width: 656,
                  height: 720,
                });
              else openDialogFunctionHandler('LicenseDisabledDialog', { grayBackground: true, width: 656, height: 720, isLicenseKeyActivated: licenseKeyActivated });
              break;
            case 3: // Expired
              if (licenseKeyActivated && license?.type === LICENSE_TYPES.Marketplace)
                // MarketPlace dialog in case of validation
                openDialogFunctionHandler('LicenseMarketplaceDialog', {
                  grayBackground: true,
                  width: 656,
                  height: 720,
                });
              else openDialogFunctionHandler('LicenseExpiredDialog', { grayBackground: true, width: 656, height: 720, isLicenseKeyActivated: licenseKeyActivated });
              break;
            case 4: // Locked
              openDialogFunctionHandler('LicenseLockedExpiredOfflineDialog', { grayBackground: true, width: 656, height: 720, licenseState: (licenseStates as any)[licenseStateCode] });
              break;
            case 5: // Not Found
              openDialogFunctionHandler('LicenseNotFoundNoneDialog', { grayBackground: true, width: 656, height: 720, isLicenseKeyActivated: licenseKeyActivated });
              break;
            case 6: // Occupied
              openDialogFunctionHandler('LicenseOccupiedDialog', { grayBackground: true, width: 656, height: 720 });
              break;
            case 7: // reset
              await router.push({ name: 'enterLicense' });
              break;
            case 8: // ActiveOffline
              break;
            case 9: // Expired offline
              openDialogFunctionHandler('LicenseLockedExpiredOfflineDialog', { grayBackground: true, width: 656, height: 720, licenseState: (licenseStates as any)[licenseStateCode] });
              break;
            default:
              break;
          }
        },

        pleaseHoldCoordinatorServerPort: () =>
          this.openDialog('SuccessDialogV2', {
            template: {
              spinnerDots: true,
              bodyIcon: false, // Optional defualt is true
            },
            attrs: {},
            dialogTitle: {
              content: i18n.global.t('settings.dialogs.pleaseHoldCoordinatorServerPort.title'),
              class: 'ibf-h1',
              style: {
                margin: 'auto',
              },
            },
            body: {
              content: i18n.global.t('settings.dialogs.pleaseHoldCoordinatorServerPort.body'),
              class: 'ibf-h3-bold Text_Medium',
              style: {
                margin: 'auto 8px',
                textAlign: 'center',
              },
            },
            footer: {
              content: i18n.global.t('settings.dialogs.pleaseHoldCoordinatorServerPort.footer'),
              class: 'ibf-h5 Text_Medium',
              style: {
                margin: '0 15px',
                textAlign: 'center',
              },
            },

            width: 320,
            height: 293,
            okCallback: () => null,
            cancelCallback: () => null,
          }),
      };
    },
  } as any,
};
